/**
 * MemorialContactAdminModal — public "Contact us about this memorial"
 * form. Any visitor (auth or anonymous) can send a private message to
 * the Social Pulse admins about a memorial: correction requests,
 * privacy concerns, additional information, takedown appeals, or a
 * simple hello from a distant relative who spotted the memorial.
 *
 * Posts to  POST /api/memorials/{key}/contact-admin.
 * Backend:
 *   - persists the request in `memorial_contact_requests`
 *   - appends an entry to the memorial's `admin_notes[]` trail
 *   - notifies every SUPER_ADMIN_EMAILS address by email
 *
 * We deliberately keep the form very short — visitors are more likely
 * to complete it if it feels like a friendly note, not a helpdesk
 * ticket.
 */
import { useState } from "react";
import { X, MessageCircle, Loader2, Send, Paperclip, XCircle } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

const REASONS = [
  { v: "correction",       label: "Suggest a correction" },
  { v: "additional_info",  label: "Share additional information" },
  { v: "privacy_concern",  label: "Privacy concern" },
  { v: "takedown_request", label: "Takedown request" },
  { v: "duplicate",        label: "This looks like a duplicate memorial" },
  { v: "general_question", label: "General question" },
  { v: "other",            label: "Other" },
];

export default function MemorialContactAdminModal({ memorial, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("general_question");
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);   // {name, kind, dataUrl}
  const [busy, setBusy] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Please attach a file under 8 MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAttachment({
      name: f.name,
      kind: f.type.startsWith("image/") ? "image" : "file",
      dataUrl: reader.result,
      size: f.size,
    });
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2)              return toast.error("Please tell us your name.");
    if (!email.trim() && !phone.trim())      return toast.error("Please share an email or phone so we can reach you.");
    if (message.trim().length < 5)           return toast.error("Please write a short message so we can help.");
    setBusy(true);
    try {
      const key = memorial.slug || memorial.id;
      await http.post(`/memorials/${key}/contact-admin`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        reason,
        message: message.trim(),
        ...(attachment && {
          attachment_data_url: attachment.dataUrl,
          attachment_name: attachment.name,
          attachment_kind: attachment.kind,
        }),
      });
      toast.success("Thank you — our team will review your message and follow up.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sorry — couldn't send that just now. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="wofy-card w-full max-w-lg text-left"
        data-testid="mem-contact-admin-modal"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="pr-3">
            <div className="overline" style={{ color: "var(--wofy-primary)" }}>CONTACT US</div>
            <h3 className="font-display text-lg mt-0.5">
              Send our team a note about {memorial?.full_name || "this memorial"}
            </h3>
            <p className="text-xs opacity-70 mt-1">
              An admin will read your note and follow up. Your details are private and won't be published on the memorial page.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 opacity-60 hover:opacity-100" data-testid="mem-contact-admin-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium mb-1 block opacity-70">Your name *</label>
            <input
              className="wofy-input" maxLength={200}
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Anita George"
              data-testid="mem-contact-admin-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium mb-1 block opacity-70">Email</label>
              <input
                type="email" className="wofy-input" maxLength={200}
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="mem-contact-admin-email"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block opacity-70">Phone</label>
              <input
                type="tel" className="wofy-input" maxLength={60}
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 …"
                data-testid="mem-contact-admin-phone"
              />
            </div>
          </div>
          <div className="text-[10px] opacity-60 -mt-1">Provide at least one of email or phone.</div>

          <div>
            <label className="text-xs font-medium mb-1 block opacity-70">Reason for contacting us</label>
            <select
              className="wofy-input" value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid="mem-contact-admin-reason"
            >
              {REASONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium mb-1 block opacity-70">Your message *</label>
            <textarea
              dir="auto" className="wofy-input" rows={5} maxLength={4000}
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Please share the details we should know. Any language is fine."
              data-testid="mem-contact-admin-message"
            />
          </div>

          {/* Optional attachment */}
          <div>
            <label className="text-xs font-medium mb-1 block opacity-70 flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> Attach a photo or document (optional)
            </label>
            {attachment ? (
              <div className="rounded-lg border p-2 flex items-center gap-2 text-xs" style={{ borderColor: "var(--wofy-border)", background: "#FAFAF7" }}>
                {attachment.kind === "image" ? (
                  <img src={attachment.dataUrl} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center flex-shrink-0"><Paperclip className="w-5 h-5 opacity-60" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate">{attachment.name}</div>
                  <div className="opacity-60">{Math.round(attachment.size / 1024)} KB</div>
                </div>
                <button type="button" onClick={() => setAttachment(null)} className="opacity-60 hover:opacity-100" data-testid="mem-contact-admin-attach-remove">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={onFile}
                className="text-xs block"
                data-testid="mem-contact-admin-attach-input"
              />
            )}
            <div className="text-[10px] opacity-60 mt-1">Great for scanned obituaries, old family photos, or supporting docs. Max 8 MB.</div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="mem-contact-admin-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send message</>}
          </button>
        </div>
      </form>
    </div>
  );
}
